// Ported from vlookup-dramatic/app/mentorlookup/feature-showcase.tsx by scripts/revamp/port-design.js
import React, { useState } from "react";
import Image from "../../components/image";
import { Icon } from "../../components/icon";
import { mentorLookUp, mluFeatureImages } from "../../lib/content";
const features = mentorLookUp.features;
const pad = (n) => String(n).padStart(2, "0");
// Databricks-Lakebase-style feature showcase: a vertical list of expandable
// rows on the left (one open at a time, with a progress bar that drives the
// ~5s auto-advance) and an image panel on the right that crossfades to match
// the active row. Hover/focus pauses; clicking selects; reduced motion
// disables the auto-advance entirely (the progress bar never animates, so
// onAnimationEnd never fires).
export function MluFeatureShowcase() {
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);
    const advance = () => setActive((a) => (a + 1) % features.length);
    return (<div className="grid items-stretch gap-6 lg:grid-cols-[0.95fr_1.05fr] lg:gap-10" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)}>
      {/* Keyframes + the paused state live here: the animate-[…] utility is !important
          (scoped stylesheet), so an inline animation-play-state would lose. This rule
          has the same specificity and comes later in the document, so it wins. */}
      <style>{`
        @keyframes mluFeatProgress { from { width: 0%; } to { width: 100%; } }
        .vl-revamp .mluFeatProgress[data-paused="true"] { animation-play-state: paused !important; }
      `}</style>

      {/* Left — expandable rows */}
      <div className="flex flex-col gap-2">
        {features.map((f, i) => {
            const isActive = i === active;
            return (<button key={f.title} type="button" onClick={() => setActive(i)} aria-expanded={isActive} className={`group relative overflow-hidden rounded-2xl border text-left transition-all duration-300 ${isActive
                    ? "border-brand-navy/10 bg-card shadow-lg"
                    : "border-transparent hover:bg-card/70"}`}>
              <span className="flex items-center gap-4 px-5 py-3.5">
                <Icon name={f.icon} className={`shrink-0 transition-all duration-300 ${isActive ? "size-11" : "size-9 opacity-60 grayscale"}`}/>
                <span className={`font-display text-lg font-semibold transition-colors ${isActive ? "text-brand-navy" : "text-brand-navy/55"}`}>
                  {f.title}
                </span>
              </span>
              {/* expanding description */}
              <span className={`grid transition-[grid-template-rows] duration-500 ease-out motion-reduce:transition-none ${isActive ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                <span className="block overflow-hidden">
                  <span className="block px-5 pb-4 pl-[4.75rem] text-sm leading-relaxed text-muted-foreground">
                    {f.desc}
                  </span>
                </span>
              </span>
              {/* progress indicator drives the auto-advance */}
              {isActive && (<span key={`progress-${active}`} aria-hidden data-paused={paused ? "true" : "false"} onAnimationEnd={advance} className="mluFeatProgress absolute bottom-0 left-0 block h-0.5 animate-[mluFeatProgress_5s_linear_forwards] bg-brand-red motion-reduce:animate-none"/>)}
            </button>);
        })}
      </div>

      {/* Right — crossfading image panel keyed to the active row.
          QA (31 Aug): hidden below lg — stacked under the rows it lost its
          "follows along" meaning and read as a stray stand-alone photo. */}
      <div className="relative hidden aspect-[4/3] overflow-hidden rounded-[2rem] border border-brand-navy/10 bg-brand-ink shadow-xl lg:block lg:aspect-auto">
        {features.map((f, i) => (<Image key={f.title} src={mluFeatureImages[f.title] ?? "/revamp/images/g-programme.webp"} alt={f.title} fill sizes="(max-width: 1024px) 92vw, 560px" className={`object-cover transition-all duration-700 ease-out motion-reduce:transition-none ${i === active ? "scale-100 opacity-100" : "scale-[1.04] opacity-0"}`}/>))}
        <div aria-hidden className="absolute inset-0 scrim-overlay"/>
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 md:p-7">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-cream/60">
              Feature {pad(active + 1)} / {pad(features.length)}
            </p>
            <p className="mt-1.5 font-display text-2xl font-semibold text-white md:text-3xl">
              {features[active].title}
            </p>
          </div>
          <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
            {features.map((f, i) => (<button key={f.title} type="button" aria-label={`Show feature: ${f.title}`} aria-current={i === active} onClick={() => setActive(i)} className={`h-1.5 rounded-full transition-all ${i === active
                ? "w-6 bg-brand-red"
                : "w-1.5 bg-white/40 hover:bg-white/70"}`}/>))}
          </div>
        </div>
      </div>
    </div>);
}
